import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import { EmailDirection } from './lossRunRequest'

export { EmailDirection }

/** `ANY` is a sentinel, not an empty string — Radix `SelectItem` rejects `''`. */
export enum EmailAttachmentFilter {
  ANY = 'any',
  WITH = 'true',
  WITHOUT = 'false',
}

export interface EmailThreadOrg {
  id: string
  name: string
}

export interface EmailThreadSummary {
  threadKey: string
  subject: string
  snippet: string
  participants: string[]
  lastDate: string
  messageCount: number
  direction: EmailDirection
  hasAttachments: boolean
  bounced: boolean
  org: EmailThreadOrg | null
}

export interface EmailThreadPage {
  count: number
  /** Page numbers, not URLs — the endpoint pages over threads, not messages. */
  next: number | null
  previous: number | null
  results: EmailThreadSummary[]
}

export interface EmailAttachment {
  id: string
  fileName: string
  fileExtension: string
  fileSize: number
  fileUrl: string | null
}

export enum QaOutcomeCode {
  DISPATCHED = 'DISPATCHED',
  STORED_NO_DISPATCH = 'STORED_NO_DISPATCH',
  STORED_NO_FOLLOWUP = 'STORED_NO_FOLLOWUP',
  DROPPED_OWN_INBOX = 'DROPPED_OWN_INBOX',
  DROPPED_BOUNCE = 'DROPPED_BOUNCE',
  DROPPED_UNRESOLVED = 'DROPPED_UNRESOLVED',
}

export interface QaOutcome {
  code: QaOutcomeCode
  handler?: string
}

export interface EmailThreadMessage {
  id: string
  dateCreated: string
  direction: EmailDirection
  provider: string
  fromEmail: string
  toEmail: string
  /** The address it was addressed to — `toEmail` is the mailbox it landed in. */
  toHeader: string
  ccEmail: string
  subject: string
  bodyText: string
  bodyHtml: string
  bouncedAt: string | null
  bounceReason: string
  openCount: number
  agentRunId: string | null
  relatedModel: string | null
  relatedModelId: string | null
  org: EmailThreadOrg | null
  attachments: EmailAttachment[]
  isQa: boolean
  qaOutcome: QaOutcome | null
  replyTo: string
}

export interface EmailThread {
  threadKey: string
  subject: string
  participants: string[]
  messageCount: number
  org: EmailThreadOrg | null
  messages: EmailThreadMessage[]
}

export interface EmailThreadFilters {
  q?: string
  fromEmail?: string
  hasAttachments?: EmailAttachmentFilter
  page?: number
}

function getEmailThreads(filters: EmailThreadFilters): Promise<EmailThreadPage> {
  const params: Record<string, string> = {}
  if (filters.q) params.q = filters.q
  if (filters.fromEmail) params.from_email = filters.fromEmail
  if (filters.hasAttachments && filters.hasAttachments !== EmailAttachmentFilter.ANY) {
    params.has_attachments = filters.hasAttachments
  }
  if (filters.page && filters.page > 1) params.page = String(filters.page)
  return axios
    .get('/api/v1/insure/internal/emails/threads', { params })
    .then((r) => toCamelCase(r.data) as EmailThreadPage)
}

export function useEmailThreads(filters: EmailThreadFilters) {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_THREADS, filters],
    queryFn: () => getEmailThreads(filters),
    placeholderData: (previous) => previous,
  })
}

function getEmailThread(threadKey: string): Promise<EmailThread> {
  return axios
    .get(`/api/v1/insure/internal/emails/threads/${encodeURIComponent(threadKey)}`)
    .then((r) => toCamelCase(r.data) as EmailThread)
}

export function useEmailThread(threadKey: string | null) {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_THREAD, threadKey],
    queryFn: () => getEmailThread(threadKey!),
    enabled: !!threadKey,
  })
}

function getEmailSenders(): Promise<string[]> {
  return axios.get('/api/v1/insure/internal/emails/senders').then((r) => r.data.senders as string[])
}

export function useEmailSenders() {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_SENDERS],
    queryFn: getEmailSenders,
    staleTime: 5 * 60 * 1000,
  })
}
